import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { Calendar, Clock, ArrowLeft, MessageCircle, ChevronRight } from "lucide-react";
import { type BlogPost } from "@/lib/blog-posts";
import { getPosts, getPostBySlug, BLOG_UI, type Locale } from "@/lib/blog-posts-i18n";
import { KENTRO } from "@/lib/constants";
import { BlogNotFound, BlogErrorState } from "@/components/blog/BlogNotFound";

const SITE = "https://kentro.com.br";

export const Route = createFileRoute("/$locale/blog/$slug")({
  loader: ({ params }) => {
    const locale: Locale = params.locale === "es" ? "es" : "en";
    const post = getPostBySlug(locale, params.slug);
    if (!post) throw notFound();
    return { slug: post.slug, locale };
  },
  head: ({ params }) => {
    const locale: Locale = params.locale === "es" ? "es" : "en";
    const post = getPostBySlug(locale, params.slug);
    if (!post) return { meta: [{ title: "Post — Kentro" }] };
    const url = `${SITE}/${locale}/blog/${params.slug}`;
    const articleImage = post.cover ? `${SITE}${post.cover}` : `${SITE}/og-image.png`;
    const articleLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${url}#article`,
      url,
      headline: post.title,
      name: post.title,
      description: post.description,
      image: [articleImage],
      datePublished: post.date,
      dateModified: post.date,
      inLanguage: locale === "es" ? "es" : "en",
      articleSection: post.category,
      wordCount: post.readingMinutes * 200,
      timeRequired: `PT${post.readingMinutes}M`,
      author: {
        "@type": "Organization",
        name: post.author,
        url: SITE,
      },
      publisher: {
        "@type": "Organization",
        name: "Kentro Sistemas",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      keywords: post.keyword,
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: BLOG_UI[locale].breadcrumbHome, item: `${SITE}/${locale}` },
        { "@type": "ListItem", position: 2, name: BLOG_UI[locale].breadcrumbBlog, item: `${SITE}/${locale}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    };
    const scripts: { type: string; children: string }[] = [
      { type: "application/ld+json", children: JSON.stringify(articleLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
    ];
    if (post.faq?.length) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }
    return {
      meta: [
        { title: `${post.title} | Kentro` },
        { name: "description", content: post.description },
        { name: "author", content: post.author },
        { name: "keywords", content: post.keyword },
        { property: "og:type", content: "article" },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.description },
        { property: "og:url", content: url },
        { property: "og:locale", content: locale === "es" ? "es_ES" : "en_US" },
        { property: "og:image", content: articleImage },
        { property: "og:image:alt", content: post.coverAlt ?? post.title },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "article:published_time", content: post.date },
        { property: "article:modified_time", content: post.date },
        { property: "article:author", content: post.author },
        { property: "article:section", content: post.category },
        { property: "article:tag", content: post.keyword },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.description },
        { name: "twitter:image", content: articleImage },
        { name: "twitter:image:alt", content: post.coverAlt ?? post.title },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "pt-BR", href: `${SITE}/blog/${params.slug}` },
        { rel: "alternate", hrefLang: "en", href: `${SITE}/en/blog/${params.slug}` },
        { rel: "alternate", hrefLang: "es", href: `${SITE}/es/blog/${params.slug}` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}/blog/${params.slug}` },
      ],
      scripts,
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => {
    const { locale: rawLocale, slug } = Route.useParams();
    const locale: Locale = rawLocale === "es" ? "es" : "en";
    return <BlogNotFound slug={slug} locale={locale} />;
  },
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    const { locale: rawLocale } = Route.useParams();
    const locale: Locale = rawLocale === "es" ? "es" : "en";
    return (
      <BlogErrorState
        error={error instanceof Error ? error : new Error(String(error))}
        reset={() => {
          router.invalidate();
          reset();
        }}
        locale={locale}
      />
    );
  },
});

function BlogPostPage() {
  const { slug, locale } = Route.useLoaderData() as { slug: string; locale: Locale };
  const post = getPostBySlug(locale, slug) as BlogPost;
  const ui = BLOG_UI[locale];
  const allPosts = getPosts(locale);
  const related: BlogPost[] = (post.related ?? [])
    .map((s: string) => allPosts.find((p) => p.slug === s))
    .filter((p): p is BlogPost => Boolean(p));

  const formattedDate = new Date(post.date).toLocaleDateString(ui.dateLocale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <article className="mx-auto max-w-3xl px-4 pt-24 pb-16 sm:px-6 lg:px-8">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        <a href={`/${locale}`} className="hover:text-foreground">{ui.breadcrumbHome}</a>
        <ChevronRight className="size-3" />
        <a href={`/${locale}/blog`} className="hover:text-foreground">{ui.breadcrumbBlog}</a>
        <ChevronRight className="size-3" />
        <span className="text-foreground/80">{post.category}</span>
      </nav>

      <header className="mt-6">
        <span className="inline-flex rounded-full border border-border bg-card/40 px-2.5 py-1 text-xs text-muted-foreground">
          {post.category}
        </span>
        <h1 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
          {post.title}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{post.description}</p>
        <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="size-4" /> {formattedDate}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="size-4" /> {post.readingMinutes} {ui.minutes}
          </span>
          <span>{ui.by} {post.author}</span>
        </div>
      </header>

      <div className="mt-10 rounded-2xl border border-border bg-card/60 p-6 shadow-sm backdrop-blur-sm md:p-10">
        <div className="prose max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-li:text-foreground/90 prose-a:text-primary-glow hover:prose-a:underline prose-blockquote:text-foreground/80 prose-blockquote:border-l-primary-glow prose-code:text-foreground prose-hr:border-border dark:prose-invert">
          {post.content}
        </div>
      </div>

      {post.faq?.length ? (
        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{ui.faqHeading}</h2>
          <div className="mt-5 space-y-3">
            {post.faq.map((f: { q: string; a: string }) => (
              <details
                key={f.q}
                className="group rounded-xl border border-border bg-card/40 p-4 open:bg-card/60"
              >
                <summary className="cursor-pointer list-none font-semibold">
                  {f.q}
                  <span className="float-right text-muted-foreground group-open:hidden">+</span>
                  <span className="float-right text-muted-foreground hidden group-open:inline">−</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      ) : null}

      {post.internalLinks?.length ? (
        <section className="mt-10 rounded-2xl border border-border bg-card/40 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {ui.continueReading}
          </h2>
          <ul className="mt-3 space-y-1.5 text-sm">
            {post.internalLinks.map((l: { label: string; href: string }) => (
              <li key={l.href}>
                <a className="text-primary-glow hover:underline" href={l.href}>
                  {l.label} →
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="gradient-primary shadow-glow mt-12 rounded-2xl p-6 text-white md:p-8">
        <h2 className="text-xl font-bold md:text-2xl">{ui.ctaTitle}</h2>
        <p className="mt-2 text-white/85">{ui.ctaDesc}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a
            href={`/${locale}/contato`}
            className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[color:var(--primary)] hover:bg-white/90"
          >
            {ui.ctaPrimary}
          </a>
          <a
            href={KENTRO.whatsappComercialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
          >
            <MessageCircle className="size-4" /> WhatsApp (38) 3220-8706
          </a>
        </div>
      </section>

      {related.length ? (
        <section className="mt-14">
          <h2 className="text-2xl font-bold tracking-tight">{ui.related}</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {related.map((r: BlogPost) => (
              <a
                key={r.slug}
                href={`/${locale}/blog/${r.slug}`}
                className="group rounded-xl border border-border bg-card/40 p-5 transition hover:border-primary-glow/40"
              >
                <span className="text-xs text-muted-foreground">{r.category}</span>
                <h3 className="mt-2 font-semibold group-hover:text-primary-glow">{r.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{r.description}</p>
              </a>
            ))}
          </div>
        </section>
      ) : null}

      <div className="mt-12">
        <a
          href={`/${locale}/blog`}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> {ui.backToBlog}
        </a>
      </div>
    </article>
  );
}
